import React, { useState, useRef, KeyboardEvent } from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { chatService } from "../service/chatService";
import styles from "./ChatInput.module.scss";

interface ChatInputProps {
  onSend: (message: string, fid?: number) => void;
  disabled?: boolean;
  sending?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  sending = false,
}) => {
  const [message, setMessage] = useState("");
  const [attachedFile, setAttachedFile] = useState<{ name: string; fid: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const trimmed = message.trim();
    if ((!trimmed && !attachedFile) || sending || uploading) return;
    // API requires message; fall back to filename when sending attachment-only
    const messageToSend = trimmed || (attachedFile?.name ?? "");
    onSend(messageToSend, attachedFile?.fid);
    setMessage("");
    setAttachedFile(null);
    setUploadError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const fid = await chatService.uploadChatAttachment(file);
      setAttachedFile({ name: file.name, fid });
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setUploadError(null);
  };

  return (
    <div className={styles.chatInputWrap}>
      {(attachedFile || uploading || uploadError) && (
        <div className={styles.attachmentArea}>
          {uploading && (
            <div className={styles.uploadingChip}>
              <ProgressSpinner style={{ width: "14px", height: "14px" }} />
              <span>Uploading…</span>
            </div>
          )}
          {attachedFile && !uploading && (
            <div className={styles.fileChip}>
              <i className="pi pi-paperclip" />
              <span className={styles.fileName}>{attachedFile.name}</span>
              <button className={styles.removeBtn} onClick={removeAttachment} title="Remove attachment">
                <i className="pi pi-times" />
              </button>
            </div>
          )}
          {uploadError && <span className={styles.uploadError}>{uploadError}</span>}
        </div>
      )}

      <div className={styles.chatInput}>
        <button
          type="button"
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          title="Attach file"
        >
          <i className="pi pi-paperclip" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
        />
        <InputTextarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={2}
          autoResize
          disabled={disabled || sending}
          className={styles.textarea}
        />
        <Button
          icon="pi pi-send"
          onClick={handleSend}
          disabled={disabled || sending || uploading || (!message.trim() && !attachedFile)}
          loading={sending}
          className={styles.sendButton}
          tooltip="Send message (Enter)"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    </div>
  );
};
